<script>
import { onDestroy, tick } from 'svelte';
import { slide } from 'svelte/transition';
import { format, addMinutes, isToday } from 'date-fns';
import { router } from '../libs/router';
import { tooltipTop } from '../tooltip';
import { saved, storedSprints, sprint, editor, loadSprint, unloadSprint, getStatus, Status, SPRINT_OPENS, secondsLeft, percent } from '../data/sprints';
import { createSprintTotalsStore } from '../data/server';
import { user } from '../data/user';
import { number } from '../libs/helpers';
import { minute, second } from '../data/datetime';
import { autoscroll } from './autoscroll';
import { pagepad } from './pagepad';
import { paper, sanitizeText, sanitizeDelta } from '../editor';
import { deltaToHTML } from '@typewriter/view-vdom';
import Page from './Page.svelte';
import SprintTimer from './SprintTimer.svelte';
import Chat from './Chat.svelte';
import ShareResults from './ShareResults.svelte';
import ShareLink from './ShareLink.svelte';
import { connection } from '../data/connection';
import { analytics } from '../data/firebase';
import globals from '../globals';

export let sprintId;

const totals = window.totals = createSprintTotalsStore(sprint, minute);
let pageElement;
let autoScrollObserver;
let loading;
let shareLink;
let shareResults;
let justFinished = $secondsLeft > 2;

$: isGroup = sprintId && sprintId.length === 12;
$: duration = $sprint && (($sprint.end - $sprint.start) / 1000 / 60) || 0;
$: status = getStatus($sprint, $second);
$: open = status !== Status.Pending;
$: finished = status === Status.Finished;
$: editable = status === Status.Pending || status === Status.Open;
$: joined = (!isGroup || $user) && $sprint && $sprint.hasOwnProperty('wordcount');
$: enabled = open && joined;
$: enableEditor($editor, enabled);
$: updatePageElement(pageElement);
$: load();
$: secondsSinceFinished = $sprint && ($sprint.end - $second)/1000;
$: justFinished = secondsSinceFinished && secondsSinceFinished < -4 && secondsSinceFinished > -6;
$: if (justFinished && $sprint.wordcount && !localStorage.dontShowResultsShare) shareResults = 'autoOpen';

async function load() {
  loading = true;
  await loadSprint(sprintId);
  loading = false;
}

function enableEditor(editor, enabled) {
  if (!editor) return;
  if (!enabled) {
    if (editor.view.enabled) {
      editor.view.disable();
      editor.view.render();
    }
  } else if (!editor.view.enabled) {
    editor.view.enable();
    editor.view.render();
    editor.view.focus();
  }
}

function updatePageElement(element) {
  if (autoScrollObserver) autoScrollObserver();
  if (element) autoScrollObserver = autoscroll(element);
}

async function copyText(event) {
  event.preventDefault();
  const selection = $editor.selection;
  $editor.setSelection([ 0, $editor.contents.length() ]);
  await tick();
  await tick();
  await tick();
  document.execCommand('copy');
  $editor.setSelection(selection);
  if (!selection) window.getSelection().removeAllRanges();
  globals.inform('success', 'Text copied', `to your clipboard.`);
  analytics.logEvent('copied_text', { wordcount: $sprint.totalWordcount });
}

function editSprint() {
  sessionStorage.lastUrl = location.pathname;
  router.navigate(`/sprint-edit/${sprintId}`);
}

async function joinSprint() {
  const user = await getUser();
  $sprint = { ...$sprint, totalWordcount: 0, wordcount: 0 };
  analytics.logEvent('join_sprint', { listed: $sprint.listed });
  await storedSprints.save($sprint);
}

async function changeName() {
  const name = (await globals.prompt('Name', 'What is the name you would like to be known by?', { placeholder: 'Name...', value: $user.name || '' })).trim();
  if (!name || name === $user.name) return;
  await user.setName(name);
  globals.inform('success', 'Name changed', `successfully.`);
}

onDestroy(() => {
  connection.disconnect();
  updatePageElement();
  unloadSprint();
});
</script>

<div class="bar-tracker"><div class="thumb" style="width:{$percent * 100}%"></div></div>
<div class="sprint" use:pagepad>
  <div class="writing">
    {#if loading}
      <div class="loading">
        <h1>Loading...</h1>
      </div>
    {:else if $sprint}
      {#if $sprint.title}
      <h3 class="sprint-title">{sanitizeText($sprint.title)}</h3>
      {/if}
      {#if $sprint.prompt}
      <div class="prompt">
        {@html deltaToHTML(sanitizeDelta($sprint.prompt), paper)}
      </div>
      {/if}
      <div class="directions">
        <p>
          Sprint data is stored on this device for <strong>24 hours only</strong>.
          {#if isGroup && !finished}
          <span class="copy-link" use:tooltipTop={'Copy the sprint link'} on:click={() => shareLink = true}>
            <i class="icon icon-link"></i>
            <a href="/sprints/{$sprint.id}">Invite others</a>
          </span>
          {/if}
          {#if finished && ($sprint.wordcount || $totals.total)}
          <span class="copy-link" use:tooltipTop={'Share the sprint results'} on:click|preventDefault={() => shareResults = true}>
            <i class="icon icon-link"></i>
            <a href="/sprints/{$sprint.id}">Share results</a>
          </span>
          {/if}
          <span class="copy-link" use:tooltipTop={'Copy your text'} on:click={copyText}>
            <i class="icon icon-format-text"></i>
            <a href="/sprints/{$sprint.id}">Copy text</a>
          </span>
          {#if $user && $user.uid === $sprint.creator && editable}
          <span class="edit-btn" use:tooltipTop={editable ? 'You may edit the sprint anytime before it begins' : 'You can no longer edit the sprint'}>
            <button class="btn xs" on:click={editSprint}>Edit Sprint</button>
          </span>
          {/if}
        </p>
      </div>
      <div bind:this={pageElement} class="page-container">
        <Page disabled={!!$user} {sprintId}/>
        {#if !open}
        <div class="pending">
          <div>
            This sprint will not <strong>open</strong> until
            <strong>{format(new Date($sprint.start - SPRINT_OPENS), isToday(new Date($sprint.start)) ? 'p' : 'PPPp')}</strong>
            (15 minutes before start time).<br><br>Once <strong>open</strong>,
            you will be able to chat and write, but none of your content will be counted towards your sprint’s word count (or the group’s)
            until the sprint has <strong>started</strong>.
          </div>
        </div>
        {:else if !joined && finished}
        <div class="pending">
          <div>This sprint has already finished.</div>
        </div>
        {:else if !joined}
        <div class="pending">
          <div>Would you like to join this sprint?</div>
          <button class="btn primary large" on:click={joinSprint}>Join Sprint</button>
        </div>
        {/if}
      </div>
    {:else}
      <div class="not-found">
        <h1>Sprint not found</h1>
        <h4>The sprint may have expired. They are only kept for 24 hours.</h4>
      </div>
    {/if}

    <div class="footer-content">
      © Dabble Writer, Inc.
    </div>
  </div>

  <div class="meta-placeholder"></div>
  <div class="meta">
    {#if $sprint}
    <h4 class="sprint-duration">{duration < 60 ? `${duration} Minute Sprint` : `${duration / 60} Hour Sprint`}</h4>

    <div class="timer">
      <SprintTimer/>
    </div>

    <div class="details">
      {#if status === Status.Open}
      <div class="info" transition:slide|local={{ duration: 250 }}>Word Count will not start counting until the sprint begins.</div>
      {:else if status === Status.Finished}
      <div class="info" transition:slide|local={{ duration: 250 }}>Word Count does not count after the sprint is finished.</div>
      {/if}
      <div class="detail large" use:tooltipTop={'Sprint word count'}>
        <div class="value">{number($sprint.wordcount || 0)}</div>
        <div class="label">Word Count</div>
      </div>
      {#if $sprint.totalWordcount !== $sprint.wordcount}
      <div class="detail light" use:tooltipTop={'Total word count including words before/after the sprint'} transition:slide|local={{ duration: 150 }}>
        <div class="value">{number($sprint.totalWordcount || 0)}</div>
        <div class="label">Total Word Count</div>
      </div>
      {/if}
      {#if isGroup}
      <div class="seperator"></div>
      <div class="detail">
        <div class="value">{number($totals.participants || 0)}</div>
        <div class="label">Participants</div>
      </div>
      <div class="detail">
        <div class="value">{number($totals.total || 0)}</div>
        <div class="label">Group Word Count</div>
      </div>
      <div class="detail">
        <div class="value">{number($totals.participants && Math.round($totals.total / $totals.participants) || 0)}</div>
        <div class="label">Average</div>
      </div>
      {/if}
    </div>

    {#if isGroup}
    <div class="chat-actions">
      {#if $user}
      <span class="copy-link" use:tooltipTop={'Change your name'} on:click={changeName}>
        <i class="icon icon-account-circle"></i>
        <span>{$user && $user.name || ''}</span>
      </span>
      {/if}
    </div>
    <Chat bind:sprintId disabled={!enabled}/>
    {/if}
    {/if}
  </div>
</div>

{#if shareLink && $sprint}
  <ShareLink sprint={$sprint} on:close={() => shareLink = false}/>
{/if}

{#if shareResults && $sprint && $totals}
  <ShareResults sprint={$sprint} user={$user} totals={$totals} autoOpened={shareResults === 'autoOpen'} on:close={() => shareResults = false}/>
{/if}

<style>
.sprint {
  display: flex;
  max-width: 1076px;
  min-height: 100%;
  margin: 0 auto;
  padding: 16px 8px;
}
.loading {
  color: var(--warm-grey-400);
}
.writing {
  display: flex;
  flex-direction: column;
  max-width: 816px; /* 8.5 inches */
  flex: 1;
}
.directions {
  font-size: var(--font-size-100);
	color: var(--warm-grey-300);
	text-align: right;
}
.copy-link {
  font-size: var(--font-size-100);
  color: var(--warm-grey-300);
  white-space: nowrap;
  margin-left: 16px;
  cursor: pointer;
}
.copy-link a {
  color: inherit;
}
.copy-link:hover a, .copy-link:hover span {
  text-decoration: underline;
}
.copy-link .icon {
  position: relative;
  top: 2px;
}
.chat-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 4px;
}
.edit-btn {
  margin-left: 16px;
}
.page-container {
  position: relative;
  flex: 1;
}
.bar-tracker {
  position: sticky;
  top: -1px;
  z-index: 1;
  height: 5px;
  background-color: var(--warm-grey-100);
  margin-top: -1px;
}
.bar-tracker .thumb {
  height: 100%;
  background-color: var(--cyan-300);
}
.sprint-title {
  margin-top: 0;
  margin-bottom: 24px;
  color: var(--warm-grey-600);
}
.prompt {
  margin: 12px 0;
}
.meta-placeholder {
  width: 220px;
  height: 0;
  margin-left: 16px;
}
.meta {
  display: flex;
  flex-direction: column;

  position: fixed;
  width: 228px;
  top: 72px;
  right: 0;
  bottom: 16px;
}
.meta > :global(*) {
  width: 220px;
}
.sprint-duration {
  margin-top: 0;
  margin-bottom: 24px;
  font-size: var(--font-size-500);
  color: var(--warm-grey-600);
}
.not-found {
  color: var(--warm-grey-600);
}
.timer {
  margin-bottom: 32px;
}
.details {
  margin-bottom: 24px;
}
.detail {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 6px 0;
  font-size: var(--font-size-100);
}
.detail.large {
  font-size: var(--font-size-400);
}
.detail .value {
  display: flex;
  justify-content: flex-end;
  align-items: baseline;
  font-family: var(--font-header);
  font-weight: bold;
  color: var(--warm-grey-700);
  width: 84px;
}
.detail .label {
  width: 120px;
  white-space: nowrap;
  color: var(--warm-grey-500);
}
.detail.light .value {
  color: var(--warm-grey-600);
}
.detail.light .label {
  color: var(--warm-grey-400);
}
.info {
  font-size: var(--font-size-050);
  color: var(--warm-grey-400);
}
.seperator {
  border-top: 1px solid var(--warm-grey-100);
  margin: 10px 0;
}
.pending {
  position: absolute;
  display: flex;
  flex-direction: column;
  max-width: 400px;
  left: 50%;
  transform: translate(-50%, 0);
  top: 120px;
  padding: 24px 32px;
  background: #fff;
  border-radius: 6px;
  line-height: 24px;
  color: var(--warm-grey-600);
  box-shadow: 0 1px 1px rgba(0, 0, 0, .15), 0 2px 4px rgba(0, 0, 0, .2);
  cursor: default;
}
.pending .btn {
  margin-top: 16px;
}
.footer-content {
	margin: 16px auto 0;
	text-align: center;
	color: var(--warm-grey-400);
	font-size: var(--font-size-100);
}
@media (min-width: 1076px) {
  .meta {
    left: calc(50% + 304px);
    right: auto;
  }
}
@media (max-height: 780px) and (min-width: 640px) {
  .meta {
    overflow-y: auto;
    overflow-x: hidden;
  }
}
@media (max-width: 640px) {
  .sprint {
    flex-direction: column;
  }
  .writing {
    flex: 0;
  }
  .meta-placeholder {
    width: 0;
    margin-left: 0;
  }
  .meta {
    position: static;
    margin-top: 12px;
    width: auto;
  }
  .meta > :global(*) {
    width: auto;
  }
}
@media (max-width: 416px) {
  .pending {
    left: 8px;
    right: 8px;
    top: 24px;
    padding: 16px 24px;
    transform: none;
  }
}
@media print {
  .directions, .meta, .meta-placeholder, .footer-content {
    display: none;
  }
}
</style>
